import axios from 'axios';
import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - logout user
      authService.logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Country API
export const countryAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/Country?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/Country/GetAll'),
  getById: (id) => api.get(`/Country/${id}`),
  create: (data) => api.post('/Country', data),
  update: (id, data) => api.put(`/Country/${id}`, data),
  delete: (id) => api.delete(`/Country/${id}`),
  bulkDelete: (ids) => api.post('/Country/BulkDelete', ids),
  exportExcel: () => api.get('/Country/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Country/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Country/ExportPdf', { responseType: 'blob' }),
  bulkUpload: (formData) => api.post('/Country/BulkUpload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Area API
export const areaAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/Area?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/Area/GetAll'),
  getById: (id) => api.get(`/Area/${id}`),
  create: (data) => api.post('/Area', data),
  update: (id, data) => api.put(`/Area/${id}`, data),
  delete: (id) => api.delete(`/Area/${id}`),
  bulkDelete: (ids) => api.post('/Area/BulkDelete', ids),
  exportExcel: () => api.get('/Area/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Area/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Area/ExportPdf', { responseType: 'blob' }),
  getCountries: () => api.get('/Area/GetCountries'),
};

// City API
export const cityAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/City?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/City/GetAll'),
  getById: (id) => api.get(`/City/${id}`),
  create: (data) => api.post('/City', data),
  update: (id, data) => api.put(`/City/${id}`, data),
  delete: (id) => api.delete(`/City/${id}`),
  bulkDelete: (ids) => api.post('/City/BulkDelete', ids),
  exportExcel: () => api.get('/City/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/City/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/City/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/City/DownloadTemplate', { responseType: 'blob' }),
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/City/BulkUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getAreas: () => api.get('/City/GetAreas'),
};

// District API
export const districtAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/District?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/District/GetAll'),
  getById: (id) => api.get(`/District/${id}`),
  create: (data) => api.post('/District', data),
  update: (id, data) => api.put(`/District/${id}`, data),
  delete: (id) => api.delete(`/District/${id}`),
  bulkDelete: (ids) => api.post('/District/BulkDelete', ids),
  exportExcel: () => api.get('/District/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/District/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/District/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/District/DownloadTemplate', { responseType: 'blob' }),
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/District/BulkUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getCities: () => api.get('/District/GetCities'),
};

// Organization API
export const organizationAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/Organization?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/Organization/GetAll'),
  getById: (id) => api.get(`/Organization/${id}`),
  create: (data) => api.post('/Organization', data),
  createWithFile: (formData) => api.post('/Organization', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  update: (id, data) => api.put(`/Organization/${id}`, data),
  updateWithFile: (id, formData) => api.put(`/Organization/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  delete: (id) => api.delete(`/Organization/${id}`),
  bulkDelete: (ids) => api.post('/Organization/BulkDelete', ids),
  exportExcel: () => api.get('/Organization/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Organization/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Organization/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/Organization/DownloadTemplate', { responseType: 'blob' }),
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/Organization/BulkUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getCountries: () => api.get('/Organization/GetCountries'),
  getAreas: () => api.get('/Organization/GetAreas'),
  getCities: () => api.get('/Organization/GetCities'),
  getDistricts: () => api.get('/Organization/GetDistricts'),
};

// Branch API
export const branchAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/Branch?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/Branch/GetAll'),
  getById: (id) => api.get(`/Branch/${id}`),
  create: (data) => api.post('/Branch', data),
  update: (id, data) => api.put(`/Branch/${id}`, data),
  delete: (id) => api.delete(`/Branch/${id}`),
  bulkDelete: (ids) => api.post('/Branch/BulkDelete', ids),
  exportExcel: () => api.get('/Branch/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Branch/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Branch/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/Branch/DownloadTemplate', { responseType: 'blob' }),
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/Branch/BulkUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getOrganizations: () => api.get('/Branch/GetOrganizations'),
  getCountries: () => api.get('/Branch/GetCountries'),
  getAreas: () => api.get('/Branch/GetAreas'),
  getCities: () => api.get('/Branch/GetCities'),
  getDistricts: () => api.get('/Branch/GetDistricts'),
};

// Bank API
export const bankAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/Bank?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/Bank/GetAll'),
  getById: (id) => api.get(`/Bank/${id}`),
  create: (data) => api.post('/Bank', data),
  update: (id, data) => api.put(`/Bank/${id}`, data),
  delete: (id) => api.delete(`/Bank/${id}`),
  bulkDelete: (ids) => api.post('/Bank/BulkDelete', ids),
  exportExcel: () => api.get('/Bank/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Bank/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Bank/ExportPdf', { responseType: 'blob' }),
};

// UnitType API
export const unitTypeAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/UnitType?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/UnitType/GetAll'),
  getById: (id) => api.get(`/UnitType/${id}`),
  create: (data) => api.post('/UnitType', data),
  update: (id, data) => api.put(`/UnitType/${id}`, data),
  delete: (id) => api.delete(`/UnitType/${id}`),
  bulkDelete: (ids) => api.post('/UnitType/BulkDelete', ids),
  exportExcel: () => api.get('/UnitType/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/UnitType/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/UnitType/ExportPdf', { responseType: 'blob' }),
};

// Nationality API
export const nationalityAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/Nationality?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/Nationality/GetAll'),
  getById: (id) => api.get(`/Nationality/${id}`),
  create: (data) => api.post('/Nationality', data),
  update: (id, data) => api.put(`/Nationality/${id}`, data),
  delete: (id) => api.delete(`/Nationality/${id}`),
  bulkDelete: (ids) => api.post('/Nationality/BulkDelete', ids),
  exportExcel: () => api.get('/Nationality/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Nationality/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Nationality/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/Nationality/DownloadTemplate', { responseType: 'blob' }),
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/Nationality/BulkUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
};

// Identity API
export const identityAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/Identity?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/Identity/GetAll'),
  getById: (id) => api.get(`/Identity/${id}`),
  create: (data) => api.post('/Identity', data),
  update: (id, data) => api.put(`/Identity/${id}`, data),
  delete: (id) => api.delete(`/Identity/${id}`),
  bulkDelete: (ids) => api.post('/Identity/BulkDelete', ids),
  exportExcel: () => api.get('/Identity/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Identity/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Identity/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/Identity/DownloadTemplate', { responseType: 'blob' }),
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/Identity/BulkUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
};

// FacilitiesAndAmenities API
export const facilitiesAndAmenitiesAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/FacilitiesAndAmenities?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/FacilitiesAndAmenities/GetAll'),
  getById: (id) => api.get(`/FacilitiesAndAmenities/${id}`),
  create: (data) => api.post('/FacilitiesAndAmenities', data),
  update: (id, data) => api.put(`/FacilitiesAndAmenities/${id}`, data),
  delete: (id) => api.delete(`/FacilitiesAndAmenities/${id}`),
  bulkDelete: (ids) => api.post('/FacilitiesAndAmenities/BulkDelete', ids),
  exportExcel: () => api.get('/FacilitiesAndAmenities/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/FacilitiesAndAmenities/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/FacilitiesAndAmenities/ExportPdf', { responseType: 'blob' }),
};

// RealEstateUsage API
export const realEstateUsageAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/RealEstateUsage?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/RealEstateUsage/GetAll'),
  getById: (id) => api.get(`/RealEstateUsage/${id}`),
  create: (data) => api.post('/RealEstateUsage', data),
  update: (id, data) => api.put(`/RealEstateUsage/${id}`, data),
  delete: (id) => api.delete(`/RealEstateUsage/${id}`),
  bulkDelete: (ids) => api.post('/RealEstateUsage/BulkDelete', ids),
  exportExcel: () => api.get('/RealEstateUsage/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/RealEstateUsage/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/RealEstateUsage/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/RealEstateUsage/DownloadTemplate', { responseType: 'blob' }),
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/RealEstateUsage/BulkUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
};

// Property Type API
export const realEstateTypeAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/RealEstateType?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/RealEstateType/GetAll'),
  getById: (id) => api.get(`/RealEstateType/${id}`),
  create: (data) => api.post('/RealEstateType', data),
  update: (id, data) => api.put(`/RealEstateType/${id}`, data),
  delete: (id) => api.delete(`/RealEstateType/${id}`),
  bulkDelete: (ids) => api.post('/RealEstateType/BulkDelete', ids),
  exportExcel: () => api.get('/RealEstateType/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/RealEstateType/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/RealEstateType/ExportPdf', { responseType: 'blob' }),
};

// UnitUsage API
export const unitUsageAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/UnitUsage?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/UnitUsage/GetAll'),
  getById: (id) => api.get(`/UnitUsage/${id}`),
  create: (data) => api.post('/UnitUsage', data),
  update: (id, data) => api.put(`/UnitUsage/${id}`, data),
  delete: (id) => api.delete(`/UnitUsage/${id}`),
  bulkDelete: (ids) => api.post('/UnitUsage/BulkDelete', ids),
  exportExcel: () => api.get('/UnitUsage/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/UnitUsage/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/UnitUsage/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/UnitUsage/DownloadTemplate', { responseType: 'blob' }),
  uploadExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/UnitUsage/BulkUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
};

// Unit Facilities and Amenities API
export const unitFacilitiesAndAmenitiesAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/UnitFacilitiesAndAmenities?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/UnitFacilitiesAndAmenities/GetAll'),
  getById: (id) => api.get(`/UnitFacilitiesAndAmenities/${id}`),
  create: (data) => api.post('/UnitFacilitiesAndAmenities', data),
  update: (id, data) => api.put(`/UnitFacilitiesAndAmenities/${id}`, data),
  delete: (id) => api.delete(`/UnitFacilitiesAndAmenities/${id}`),
  bulkDelete: (ids) => api.post('/UnitFacilitiesAndAmenities/BulkDelete', ids),
  exportExcel: () => api.get('/UnitFacilitiesAndAmenities/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/UnitFacilitiesAndAmenities/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/UnitFacilitiesAndAmenities/ExportPdf', { responseType: 'blob' }),
};

// Property API
export const propertyAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10, branchId = null) => {
    let url = `/Property?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (branchId !== null && branchId !== undefined) {
      url += `&branchId=${branchId}`;
    }
    return api.get(url);
  },
  getAllForExport: () => api.get('/Property/GetAll'),
  getById: (id) => api.get(`/Property/${id}`),
  getForPrint: (id) => api.get(`/Property/${id}/ForPrint`),
  create: (data) => api.post('/Property', data),
  update: (id, data) => api.put(`/Property/${id}`, data),
  delete: (id) => api.delete(`/Property/${id}`),
  bulkDelete: (ids) => api.post('/Property/BulkDelete', ids),
  exportExcel: () => api.get('/Property/ExportExcel', { responseType: 'blob' }),
  // Tab-specific save endpoints
  saveTab2: (id, data) => api.put(`/Property/${id}/Tab2`, data),
  saveTab3: (id, data) => api.put(`/Property/${id}/Tab3`, data),
  saveTab4: (id, data) => api.put(`/Property/${id}/Tab4`, data),
  saveTab5: (id, formData) => api.post(`/Property/${id}/Tab5`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDropdowns: () => api.get('/Property/GetDropdowns'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (branchId = null) => {
    let url = '/Dashboard/Stats';
    if (branchId !== null && branchId !== undefined) {
      url += `?branchId=${branchId}`;
    }
    return api.get(url);
  },
  getCharts: (branchId = null) => {
    let url = '/Dashboard/Charts';
    if (branchId !== null && branchId !== undefined) {
      url += `?branchId=${branchId}`;
    }
    return api.get(url);
  },
  getRecent: (branchId = null) => {
    let url = '/Dashboard/Recent';
    if (branchId !== null && branchId !== undefined) {
      url += `?branchId=${branchId}`;
    }
    return api.get(url);
  },
};

// Contract API
export const contractAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10, branchId = null, propertyId = null) => {
    let url = `/Contract?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (branchId !== null && branchId !== undefined) {
      url += `&branchId=${branchId}`;
    }
    if (propertyId !== null && propertyId !== undefined) {
      url += `&propertyId=${propertyId}`;
    }
    return api.get(url);
  },
  getById: (id) => api.get(`/Contract/${id}`),
  getForPrint: (id) => api.get(`/Contract/${id}/ForPrint`),
  create: (data) => api.post('/Contract', data),
  update: (id, data) => api.put(`/Contract/${id}`, data),
  delete: (id) => api.delete(`/Contract/${id}`),
  // Tab-specific save endpoints
  saveContract: (data) => api.post('/Contract/SaveContract', data), // Tab 1: Select Property
  saveContractUnits: (data) => api.post('/Contract/SaveContractUnits', data), // Tab 2: Select Units
  saveContractParties: (data) => api.post('/Contract/SaveContractParties', data), // Tab 3: Contract Parties
  saveFinancialTerms: (data) => api.post('/Contract/SaveFinancialTerms', data), // Tab 4: Financial Terms
  getFinancialTermsSummary: (contractId) => api.get(`/Contract/GetFinancialTermsSummary?contractId=${contractId}`),
  // Property selection and data
  getPropertiesWithDetails: (filters) => api.get('/Contract/GetPropertiesWithDetails', { params: filters }),
  getPropertyUnits: (propertyId) => api.get(`/Contract/GetPropertyUnits?propertyId=${propertyId}`),
  getPropertyOwnerships: (propertyId) => api.get(`/Contract/GetPropertyOwnerships?propertyId=${propertyId}`),
  createPropertyOwnership: (data) => api.post('/Contract/CreatePropertyOwnership', data),
  getUnitFacilities: (unitId) => api.get(`/Contract/GetUnitFacilities?unitId=${unitId}`),
  exportExcel: () => api.get('/Contract/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Contract/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Contract/ExportPdf', { responseType: 'blob' }),
  bulkDelete: (ids) => api.post('/Contract/BulkDelete', ids),
};

// Brokerage Agreement API
export const brokerageAgreementAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/BrokerageAgreement?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getById: (id) => api.get(`/BrokerageAgreement/${id}`),
  create: (data) => api.post('/BrokerageAgreement', data),
  update: (id, data) => api.put(`/BrokerageAgreement/${id}`, data),
  delete: (id) => api.delete(`/BrokerageAgreement/${id}`),
  // Tab-specific save endpoints
  saveBrokerageAgreement: (data) => api.post('/BrokerageAgreement/SaveBrokerageAgreement', data), // Tab 1: Select Property
  saveBrokerageAgreementUnits: (data) => api.post('/BrokerageAgreement/SaveBrokerageAgreementUnits', data), // Tab 2: Select Units
  saveBrokerageAgreementParties: (data) => api.post('/BrokerageAgreement/SaveBrokerageAgreementParties', data), // Tab 3: Select Parties
  // Property selection and data
  getPropertiesWithDetails: (filters) => api.get('/BrokerageAgreement/GetPropertiesWithDetails', { params: filters }),
  getPropertyUnits: (propertyId) => api.get(`/BrokerageAgreement/GetPropertyUnits?propertyId=${propertyId}`),
  getPropertyOwnerships: (propertyId) => api.get(`/BrokerageAgreement/GetPropertyOwnerships?propertyId=${propertyId}`),
  getOrganizations: () => api.get('/BrokerageAgreement/GetOrganizations'),
  // Tab-specific save endpoints
  saveBrokerageAgreementScope: (data) => api.post('/BrokerageAgreement/SaveBrokerageAgreementScope', data), // Tab 4: Agreement Scope
  saveBrokerageAgreementTerms: (data) => api.post('/BrokerageAgreement/SaveBrokerageAgreementTerms', data), // Tab 5: Additional Terms
  getDefaultTerms: () => api.get('/BrokerageAgreement/GetDefaultTerms'),
  exportExcel: () => api.get('/BrokerageAgreement/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/BrokerageAgreement/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/BrokerageAgreement/ExportPdf', { responseType: 'blob' }),
};

// Additional Income Report API
export const additionalIncomeReportAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10, dateFrom = null, dateTo = null) => {
    let url = `/AdditionalIncomeReport?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    return api.get(url);
  },
  getSummary: (dateFrom = null, dateTo = null) => {
    let url = '/AdditionalIncomeReport/Summary';
    const params = [];
    if (dateFrom) params.push(`dateFrom=${dateFrom}`);
    if (dateTo) params.push(`dateTo=${dateTo}`);
    if (params.length > 0) url += '?' + params.join('&');
    return api.get(url);
  },
  getAllForExport: () => api.get('/AdditionalIncomeReport/GetAll'),
  getById: (id) => api.get(`/AdditionalIncomeReport/${id}`),
  create: (data) => api.post('/AdditionalIncomeReport', data),
  update: (id, data) => api.put(`/AdditionalIncomeReport/${id}`, data),
  delete: (id) => api.delete(`/AdditionalIncomeReport/${id}`),
  bulkDelete: (ids) => api.post('/AdditionalIncomeReport/BulkDelete', ids),
  exportExcel: () => api.get('/AdditionalIncomeReport/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/AdditionalIncomeReport/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/AdditionalIncomeReport/ExportPdf', { responseType: 'blob' }),
};

// Property Management Income API
export const propertyManagementIncomeAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10, dateFrom = null, dateTo = null) => {
    let url = `/PropertyManagementIncome?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    return api.get(url);
  },
  getSummary: (dateFrom = null, dateTo = null) => {
    let url = '/PropertyManagementIncome/Summary';
    const params = [];
    if (dateFrom) params.push(`dateFrom=${dateFrom}`);
    if (dateTo) params.push(`dateTo=${dateTo}`);
    if (params.length > 0) url += '?' + params.join('&');
    return api.get(url);
  },
  getAllForExport: () => api.get('/PropertyManagementIncome/GetAll'),
  getById: (id) => api.get(`/PropertyManagementIncome/${id}`),
  create: (data) => api.post('/PropertyManagementIncome', data),
  update: (id, data) => api.put(`/PropertyManagementIncome/${id}`, data),
  delete: (id) => api.delete(`/PropertyManagementIncome/${id}`),
  bulkDelete: (ids) => api.post('/PropertyManagementIncome/BulkDelete', ids),
  exportExcel: () => api.get('/PropertyManagementIncome/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/PropertyManagementIncome/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/PropertyManagementIncome/ExportPdf', { responseType: 'blob' }),
};

// Property Expense API
export const propertyExpenseAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10, dateFrom = null, dateTo = null) => {
    let url = `/PropertyExpense?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    return api.get(url);
  },
  getSummary: (dateFrom = null, dateTo = null) => {
    let url = '/PropertyExpense/Summary';
    const params = [];
    if (dateFrom) params.push(`dateFrom=${dateFrom}`);
    if (dateTo) params.push(`dateTo=${dateTo}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return api.get(url);
  },
  getAllForExport: () => api.get('/PropertyExpense/GetAll'),
  getById: (id) => api.get(`/PropertyExpense/${id}`),
  create: (data) => api.post('/PropertyExpense', data),
  update: (id, data) => api.put(`/PropertyExpense/${id}`, data),
  delete: (id) => api.delete(`/PropertyExpense/${id}`),
  bulkDelete: (ids) => api.post('/PropertyExpense/BulkDelete', ids),
  exportExcel: () => api.get('/PropertyExpense/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/PropertyExpense/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/PropertyExpense/ExportPdf', { responseType: 'blob' }),
  printSingle: (id) => api.get(`/PropertyExpense/PrintSingle/${id}`, { responseType: 'blob' }),
  printReport: () => api.get('/PropertyExpense/PrintReport', { responseType: 'blob' }),
};

// Office Expense API
export const officeExpenseAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10, dateFrom = null, dateTo = null) => {
    let url = `/OfficeExpense?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    return api.get(url);
  },
  getSummary: (dateFrom = null, dateTo = null) => {
    let url = '/OfficeExpense/Summary';
    const params = [];
    if (dateFrom) params.push(`dateFrom=${dateFrom}`);
    if (dateTo) params.push(`dateTo=${dateTo}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return api.get(url);
  },
  getAllForExport: () => api.get('/OfficeExpense/GetAll'),
  getById: (id) => api.get(`/OfficeExpense/${id}`),
  create: (data) => api.post('/OfficeExpense', data),
  update: (id, data) => api.put(`/OfficeExpense/${id}`, data),
  delete: (id) => api.delete(`/OfficeExpense/${id}`),
  bulkDelete: (ids) => api.post('/OfficeExpense/BulkDelete', ids),
  exportExcel: () => api.get('/OfficeExpense/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/OfficeExpense/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/OfficeExpense/ExportPdf', { responseType: 'blob' }),
  printSingle: (id) => api.get(`/OfficeExpense/PrintSingle/${id}`, { responseType: 'blob' }),
  printReport: () => api.get('/OfficeExpense/PrintReport', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/OfficeExpense/DownloadTemplate', { responseType: 'blob' }),
  bulkUpload: (formData) => api.post('/OfficeExpense/BulkUpload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Brokerage API
export const brokerageAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10, branchId = null) => {
    let url = `/Brokerage?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`;
    if (branchId !== null && branchId !== undefined) {
      url += `&branchId=${branchId}`;
    }
    return api.get(url);
  },
  getAllForExport: () => api.get('/Brokerage/GetAll'),
  getById: (id) => api.get(`/Brokerage/${id}`),
  create: (data) => api.post('/Brokerage', data),
  update: (id, data) => api.put(`/Brokerage/${id}`, data),
  delete: (id) => api.delete(`/Brokerage/${id}`),
  bulkDelete: (ids) => api.post('/Brokerage/BulkDelete', ids),
  exportExcel: () => api.get('/Brokerage/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/Brokerage/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/Brokerage/ExportPdf', { responseType: 'blob' }),
  downloadTemplate: () => api.get('/Brokerage/DownloadTemplate', { responseType: 'blob' }),
  bulkUpload: (formData) => api.post('/Brokerage/BulkUpload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Receipt Voucher API
export const receiptVoucherAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/ReceiptVoucher?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/ReceiptVoucher/GetAll'),
  getById: (id) => api.get(`/ReceiptVoucher/${id}`),
  create: (data) => api.post('/ReceiptVoucher', data),
  update: (id, data) => api.put(`/ReceiptVoucher/${id}`, data),
  delete: (id) => api.delete(`/ReceiptVoucher/${id}`),
  bulkDelete: (ids) => api.post('/ReceiptVoucher/BulkDelete', ids),
  exportExcel: () => api.get('/ReceiptVoucher/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/ReceiptVoucher/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/ReceiptVoucher/ExportPdf', { responseType: 'blob' }),
};

// Paid Voucher API
export const paidVoucherAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/PaidVoucher?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/PaidVoucher/GetAll'),
  getById: (id) => api.get(`/PaidVoucher/${id}`),
  create: (data) => api.post('/PaidVoucher', data),
  update: (id, data) => api.put(`/PaidVoucher/${id}`, data),
  delete: (id) => api.delete(`/PaidVoucher/${id}`),
  bulkDelete: (ids) => api.post('/PaidVoucher/BulkDelete', ids),
  exportExcel: () => api.get('/PaidVoucher/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/PaidVoucher/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/PaidVoucher/ExportPdf', { responseType: 'blob' }),
};

// Residential Unit Receipt API
export const residentialUnitReceiptAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/ResidentialUnitReceipt?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/ResidentialUnitReceipt/GetAll'),
  getById: (id) => api.get(`/ResidentialUnitReceipt/${id}`),
  create: (data) => api.post('/ResidentialUnitReceipt', data),
  update: (id, data) => api.put(`/ResidentialUnitReceipt/${id}`, data),
  delete: (id) => api.delete(`/ResidentialUnitReceipt/${id}`),
  bulkDelete: (ids) => api.post('/ResidentialUnitReceipt/BulkDelete', ids),
  exportExcel: () => api.get('/ResidentialUnitReceipt/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/ResidentialUnitReceipt/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/ResidentialUnitReceipt/ExportPdf', { responseType: 'blob' }),
};

// Annual Reconciliation API
export const annualReconciliationAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 10) =>
    api.get(`/AnnualReconciliation?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`),
  getAllForExport: () => api.get('/AnnualReconciliation/GetAll'),
  getById: (id) => api.get(`/AnnualReconciliation/${id}`),
  create: (data) => api.post('/AnnualReconciliation', data),
  update: (id, data) => api.put(`/AnnualReconciliation/${id}`, data),
  delete: (id) => api.delete(`/AnnualReconciliation/${id}`),
  bulkDelete: (ids) => api.post('/AnnualReconciliation/BulkDelete', ids),
  exportExcel: () => api.get('/AnnualReconciliation/ExportExcel', { responseType: 'blob' }),
  exportCsv: () => api.get('/AnnualReconciliation/ExportCsv', { responseType: 'blob' }),
  exportPdf: () => api.get('/AnnualReconciliation/ExportPdf', { responseType: 'blob' }),
};

export const propertyUnitAPI = {
  getByProperty: (propertyId) => api.get(`/PropertyUnits?propertyId=${propertyId}`),
  getById: (id) => api.get(`/PropertyUnits/${id}`),
  create: (data) => api.post('/PropertyUnits', data),
  update: (id, data) => api.put(`/PropertyUnits/${id}`, data),
  delete: (id) => api.delete(`/PropertyUnits/${id}`),
};

// Attachments are multipart; let the browser set the boundary by overriding
// the instance's default JSON Content-Type.
const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

export const propertyAttachmentAPI = {
  getByProperty: (propertyId) => api.get(`/PropertyAttachments?propertyId=${propertyId}`),
  create: (formData) => api.post('/PropertyAttachments', formData, multipart),
  update: (id, formData) => api.put(`/PropertyAttachments/${id}`, formData, multipart),
  delete: (id) => api.delete(`/PropertyAttachments/${id}`),
};

// One-shot create: owner + documents + property + units in a single request.
export const propertyIntakeAPI = {
  getById: (id) => api.get(`/PropertyIntake/${id}`),
  create: (formData) => api.post('/PropertyIntake', formData, multipart),
  update: (id, formData) => api.put(`/PropertyIntake/${id}`, formData, multipart),
};

export const ownerAccountAPI = {
  createAccount: (data) => api.post('/OwnerAccount', data),
  getProfile: () => api.get('/OwnerAccount/Me'),
  updateProfile: (data) => api.put('/OwnerAccount/Me', data),
};

export const contractIntakeAPI = {
  getById: (id) => api.get(`/ContractIntake/${id}`),
  create: (data) => api.post('/ContractIntake', data),
  update: (id, data) => api.put(`/ContractIntake/${id}`, data),
};

export const organizationSettingsAPI = {
  get: () => api.get('/OrganizationSettings'),
  update: (data) => api.put('/OrganizationSettings', data),
};

export const propertyFinanceAPI = {
  listIncome: (propertyId) => api.get(`/PropertyFinance/Income?propertyId=${propertyId}`),
  createIncome: (data) => api.post('/PropertyFinance/Income', data),
  updateIncome: (id, data) => api.put(`/PropertyFinance/Income/${id}`, data),
  deleteIncome: (id) => api.delete(`/PropertyFinance/Income/${id}`),

  listExpense: (propertyId) => api.get(`/PropertyFinance/Expense?propertyId=${propertyId}`),
  createExpense: (data) => api.post('/PropertyFinance/Expense', data),
  updateExpense: (id, data) => api.put(`/PropertyFinance/Expense/${id}`, data),
  deleteExpense: (id) => api.delete(`/PropertyFinance/Expense/${id}`),
};

export const financialReportsAPI = {
  property: (propertyId, from, to) =>
    api.get(`/FinancialReports/Property?propertyId=${propertyId}&from=${from}&to=${to}`),
  summary: (from, to, propertyId = null) =>
    api.get(
      `/FinancialReports/Summary?from=${from}&to=${to}` +
        (propertyId ? `&propertyId=${propertyId}` : '')
    ),
  office: (from, to) => api.get(`/FinancialReports/Office?from=${from}&to=${to}`),
};

export const propertyLedgerAPI = {
  getUnitLedger: (unitId, year) =>
    api.get(`/PropertyLedger?unitId=${unitId}&year=${year}`),
  exportExcel: (unitId, year) =>
    api.get(`/PropertyLedger/ExportExcel?unitId=${unitId}&year=${year}`, {
      responseType: 'blob',
    }),
  updateInstalment: (id, data) => api.put(`/PropertyLedger/Instalment/${id}`, data),
};

export const organizationDocumentAPI = {
  getAll: () => api.get('/OrganizationDocuments'),
  create: (formData) => api.post('/OrganizationDocuments', formData, multipart),
  update: (id, formData) => api.put(`/OrganizationDocuments/${id}`, formData, multipart),
  delete: (id) => api.delete(`/OrganizationDocuments/${id}`),
};

export const staffAPI = {
  getAll: (searchTerm = '') =>
    api.get(`/Staff?searchTerm=${encodeURIComponent(searchTerm)}`),
  getById: (id) => api.get(`/Staff/${id}`),
  getModules: () => api.get('/Staff/Modules'),
  create: (data) => api.post('/Staff', data),
  update: (id, data) => api.put(`/Staff/${id}`, data),
  delete: (id) => api.delete(`/Staff/${id}`),
};

export const propertyOwnershipAPI = {
  getByProperty: (propertyId) => api.get(`/PropertyOwnerships?propertyId=${propertyId}`),
  getById: (id) => api.get(`/PropertyOwnerships/${id}`),
  create: (data) => api.post('/PropertyOwnerships', data),
  update: (id, data) => api.put(`/PropertyOwnerships/${id}`, data),
  delete: (id) => api.delete(`/PropertyOwnerships/${id}`),
};

// Owners are derived from PropertyOwnership rows, grouped server-side into
// distinct people. Used by the tree browser.
export const ownerAPI = {
  getAll: (searchTerm = '', page = 1, pageSize = 20) =>
    api.get(
      `/Owners?searchTerm=${encodeURIComponent(searchTerm)}&page=${page}&pageSize=${pageSize}`
    ),
  getProperties: (key) =>
    api.get(`/Owners/Properties?key=${encodeURIComponent(key)}`),
};

export default api;

